import type {ReactElement} from 'react';
import React from 'react';
import type {PosterGenerateAiSubtitlesOptionProps} from './poster-generate-ai-subtitles-option.types';
import {BaseGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/base-generate-ai-subtitles-option/base-generate-ai-subtitles-option';
import {getAiTranscriptParamsForAudio, getAiTranscriptParamsForVideo, getSelectableItemDataForAudioData, getSelectableItemDataForVideoData} from '@Libraries/ai-transcript.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getAudioItemsInOrder, getVideoItemsWithAudioInOrder} from '@Components/poster-editor/poster-editor-reducer';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {clearGeneratedResults, setAiSubtitlesPanelItems} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import type {SelectableItemWithAudio} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {noop} from '@Utils/general.util';

export function PosterGenerateAiSubtitlesOption({onClickGenerate = noop}: PosterGenerateAiSubtitlesOptionProps): ReactElement<any> | null {
  const dispatch = useAppDispatch();
  const videos = useAppSelector(getVideoItemsWithAudioInOrder);
  const audios = useAppSelector(getAudioItemsInOrder);
  const {getAiSubtitlesActionForItem, addAiTranscripts} = useAiTranscriptGeneration();

  const getCtaState = (): AiTranscriptAction => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return AiTranscriptAction.GENERATE;
    }

    const currentPage = poster.getCurrentPage();

    let regenerateCount = 0;
    let generatedCount = 0;
    const totalItems = audios.length + videos.length;

    for (const audio of audios) {
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForAudio(audio));
      if (action === AiTranscriptAction.REGENERATE) {
        regenerateCount += 1;
      } else if (action === AiTranscriptAction.GENERATED) {
        generatedCount += 1;
      } else {
        return AiTranscriptAction.GENERATE;
      }
    }

    for (const video of videos) {
      const loopCycles = currentPage.getDuration() / (video.endTime - video.startTime);
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(video, loopCycles));
      if (action === AiTranscriptAction.REGENERATE) {
        regenerateCount += 1;
      } else if (action === AiTranscriptAction.GENERATED) {
        generatedCount += 1;
      } else {
        return AiTranscriptAction.GENERATE;
      }
    }

    if (generatedCount === totalItems) {
      return AiTranscriptAction.GENERATED;
    }

    const totalItemsRequiringAction = totalItems - generatedCount;

    if (regenerateCount === totalItemsRequiringAction) {
      return AiTranscriptAction.REGENERATE;
    }

    return AiTranscriptAction.GENERATE;
  };

  const getItemsForAiSubtitlesGeneratingPanel = (): SelectableItemWithAudio[] => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return [];
    }

    const currentPage = poster.getCurrentPage();

    const items: SelectableItemWithAudio[] = [];

    audios.forEach((audioItem) => {
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForAudio(audioItem));
      if (action !== AiTranscriptAction.GENERATED) {
        items.push({...getSelectableItemDataForAudioData(audioItem), isSelected: true});
      }
    });

    videos.forEach((videoItem, index) => {
      const loopCycles = currentPage.getDuration() / (videoItem.endTime - videoItem.startTime);
      const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(videoItem, loopCycles));
      if (action !== AiTranscriptAction.GENERATED) {
        items.push({...getSelectableItemDataForVideoData(videoItem, loopCycles, index + 1), isSelected: true});
      }
    });

    return items;
  };

  const onGenerateAiSubtitlesClicked = async (): Promise<void> => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return;
    }

    const currentPage = poster.getCurrentPage();

    dispatch(setAiSubtitlesPanelItems(getItemsForAiSubtitlesGeneratingPanel()));

    dispatch(clearGeneratedResults());

    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_CONTEXTUAL_MENU);

    onClickGenerate();

    await addAiTranscripts([
      ...audios
        .filter((audio) => {
          const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForAudio(audio));
          return action === AiTranscriptAction.GENERATE || action === AiTranscriptAction.REGENERATE;
        })
        .map(getAiTranscriptParamsForAudio),
      ...videos
        .filter((video) => {
          const action = getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(video, currentPage.getDuration() / (video.endTime - video.startTime)));
          return action === AiTranscriptAction.GENERATE || action === AiTranscriptAction.REGENERATE;
        })
        .map((videoItem) => {
          return getAiTranscriptParamsForVideo(videoItem, currentPage.getDuration() / (videoItem.endTime - videoItem.startTime));
        }),
    ]);
  };

  if (!audios.length && !videos.length) {
    return null;
  }

  return <BaseGenerateAiSubtitlesOption ctaState={getCtaState()} onGenerateClicked={onGenerateAiSubtitlesClicked} />;
}
